import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { WritingEditor } from "@/components/WritingEditor";
import { writingPathEntry } from "@/lib/api";

// ENTRY ROUTES (Increment 6) — Student Choice · Ideas-First · Draft-First. Only the way TO a working thesis differs; from the
// main-point surface on, every route uses the same Build → Organize → Write → Strengthen machinery.
const btn = "inline-flex items-center gap-2 bg-[#8C3A2A] text-stone-50 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-[#a34635] disabled:opacity-40 transition-colors";
const ghost = "inline-flex items-center gap-2 border border-stone-400 text-stone-800 px-5 py-2.5 rounded-sm font-mono-panel text-xs uppercase tracking-[0.2em] hover:bg-stone-100 disabled:opacity-40 transition-colors";
const link = "text-[12px] text-stone-500 hover:text-[#8C3A2A] underline underline-offset-4 disabled:opacity-40";

export function RouteChoice({ entry, busy, onChoose, switching }) {
  const choices = entry.choices.filter((c) => !switching || c.route !== entry.route);
  const primary = choices.filter((c) => c.primary !== false);
  const secondary = choices.filter((c) => c.primary === false);
  return (
    <section data-testid="wp-route-choice" className="bg-white border border-stone-200 rounded-md p-6">
      <h1 className="font-serif-display text-3xl text-stone-900 mb-5">{switching ? "Try a different way to begin" : "How would you like to begin?"}</h1>
      <div className="space-y-3">
        {primary.map((c) => (
          <button key={c.route} data-testid={`wp-route-${c.route}`} onClick={() => onChoose(c.route)} disabled={busy}
            className="w-full text-left border border-stone-300 rounded-sm p-4 hover:border-[#8C3A2A] hover:bg-stone-50 transition-colors disabled:opacity-40">
            <p className="text-[16px] text-stone-900">{c.label}</p>
            <p className="text-[13px] text-stone-500 mt-1">{c.support}</p>
          </button>
        ))}
      </div>
      {secondary.length > 0 && (
        <div data-testid="wp-route-secondary" className="mt-6 border-t border-stone-200 pt-4">
          <p className="text-[13px] text-stone-700">Already started writing?</p>
          <p className="text-[12.5px] text-stone-500 mt-0.5">{secondary[0].support}</p>
          <button data-testid={`wp-route-${secondary[0].route}`} onClick={() => onChoose(secondary[0].route)} disabled={busy} className={`${link} mt-2 inline-block text-[13px]`}>{secondary[0].label} →</button>
        </div>
      )}
    </section>
  );
}

// "LET STUDENTS CHOOSE" — the only teacher setting that shows a student this choice: the two doors, before any Full Studio route.
export function DoorChoice({ entry, busy, onChoose }) {
  return (
    <section data-testid="wp-door-choice" className="bg-white border border-stone-200 rounded-md p-6">
      <h1 className="font-serif-display text-3xl text-stone-900 mb-5">How would you like to work?</h1>
      <div className="grid gap-3 sm:grid-cols-2">
        {(entry.doors || []).map((d) => (
          <button key={d.key} data-testid={`wp-door-${d.key}`} onClick={() => onChoose(d.key)} disabled={busy}
            className="text-left border border-stone-300 rounded-sm p-5 hover:border-[#8C3A2A] hover:bg-stone-50 transition-colors disabled:opacity-40">
            <p className="text-[17px] text-stone-900">{d.label}</p>
            <p className="text-[13px] text-stone-500 mt-1">{d.support}</p>
          </button>
        ))}
      </div>
    </section>
  );
}

export function SwitchRouteLink({ entry, onSwitch, busy }) {
  if (!entry?.can_switch || !entry?.route) return null;
  return <button data-testid="wp-switch-route" onClick={onSwitch} disabled={busy} className={link}>Try a different way to begin</button>;
}

// The student may move on at any point: "this is my main point" → build the argument, without waiting for TWS to recognize a thesis.
export function MoveOnWithThesis({ sessionId, entry, onMoved, busy: outerBusy }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!entry?.route) return null;
  if (!open) return <button data-testid="wp-move-on-thesis" disabled={outerBusy} onClick={() => setOpen(true)} className={link}>I have my main point — move on</button>;
  return (
    <div data-testid="wp-move-on-thesis-form" className="mt-2 space-y-2">
      <label className="block text-[13px] text-stone-700">Write your main point as one sentence. It becomes your working thesis — you can revise it later.
        <textarea data-testid="wp-move-on-thesis-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} className="mt-1 w-full border border-stone-300 rounded-sm px-3 py-2 text-[15px]" />
      </label>
      {err && <p data-testid="wp-move-on-thesis-error" className="text-[13px] text-[#8C3A2A]">{err}</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button data-testid="wp-move-on-thesis-go" disabled={busy || outerBusy} className={btn}
          onClick={async () => { setBusy(true); setErr(""); try { await writingPathEntry(sessionId, { action: "move_on", text }); await onMoved(); } catch (e) { const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong."); } finally { setBusy(false); } }}>Use this and move on</button>
        <button data-testid="wp-move-on-thesis-cancel" disabled={busy} onClick={() => setOpen(false)} className={link}>Keep developing it</button>
      </div>
    </div>
  );
}

export function IdeasFirst({ sessionId, entry, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState("");
  const [editText, setEditText] = useState("");
  const act = async (payload) => {
    if (busy) return;
    setBusy(true); setErr("");
    try { onUpdate(await writingPathEntry(sessionId, payload)); if (payload.action === "add_idea") setDraft(""); }
    catch (e) { const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong."); }
    finally { setBusy(false); }
  };
  const relating = entry.ideas_stage === "relate";
  return (
    <section data-testid="wp-ideas" className="bg-white border border-stone-200 rounded-md p-6">
      <h1 className="font-serif-display text-3xl text-stone-900 mb-2">{relating ? "What do these ideas add up to?" : "Explore your ideas"}</h1>
      <p data-testid="wp-ideas-prompt" className="text-[17px] text-stone-800 mb-1">{relating ? entry.relate_prompts[0] : entry.ideas_prompt}</p>
      {relating && <p className="text-[13px] text-stone-500 mb-4">{entry.relate_prompts.slice(1).join(" ")}</p>}
      {!relating && <p className="text-[13px] text-stone-500 mb-4">Write each idea in your own words. This is thinking material — it does not have to be organized yet.</p>}
      <ol data-testid="wp-ideas-list" className="space-y-2 mb-4">
        {entry.ideas.map((i, n) => (
          <li key={i.id} data-testid={`wp-idea-${i.id}`} className="flex items-start gap-3 border border-stone-200 rounded-sm p-3">
            <span className="font-mono-panel text-[11px] text-stone-400 mt-1 w-5">{n + 1}.</span>
            {editing === i.id ? (
              <div className="flex-1 space-y-2">
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className="w-full border border-stone-300 rounded-sm px-3 py-2 text-[15px]" />
                <div className="flex gap-3"><button onClick={async () => { await act({ action: "revise_idea", id: i.id, text: editText }); setEditing(""); }} disabled={busy || !editText.trim()} className={btn}>Save</button><button onClick={() => setEditing("")} className={ghost}>Cancel</button></div>
              </div>
            ) : (
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-stone-900">{i.text}</p>
                <div className="mt-1 flex gap-4">
                  <button data-testid={`wp-idea-${i.id}-revise`} onClick={() => { setEditing(i.id); setEditText(i.text); }} disabled={busy} className={link}>Revise</button>
                  <button data-testid={`wp-idea-${i.id}-remove`} onClick={() => act({ action: "remove_idea", id: i.id })} disabled={busy} className={link}>Remove</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ol>
      <div className="border border-dashed border-stone-300 rounded-sm p-3">
        <textarea data-testid="wp-idea-input" value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} disabled={busy} placeholder="Something that seems important…"
          className="w-full border border-stone-300 rounded-sm px-3 py-2 text-[15px] focus:outline-none focus:border-[#8C3A2A]" />
        <button data-testid="wp-idea-add" onClick={() => act({ action: "add_idea", text: draft })} disabled={busy || !draft.trim()} className={`${btn} mt-2`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Add this idea</button>
      </div>
      {err && <p data-testid="wp-ideas-error" className="mt-3 text-[13px] text-[#8C3A2A]">{err}</p>}
      {entry.ideas_coach && <div data-testid="wp-ideas-coach" className="mt-5 text-[14px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-4 leading-relaxed"><p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-1">TWS</p>{entry.ideas_coach}</div>}
      <div className="mt-6 border-t border-stone-200 pt-5 flex flex-wrap items-center gap-3">
        {!relating && <button data-testid="wp-ideas-relate" onClick={() => act({ action: "relate" })} disabled={busy || entry.ideas.length < 2} className={ghost}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} How do these ideas relate?</button>}
        <span className="flex-1" />
        <button data-testid="wp-ideas-to-thesis" onClick={() => act({ action: "to_thesis" })} disabled={busy} className={btn}>I know what I think now <ArrowRight className="h-4 w-4" /></button>
      </div>
    </section>
  );
}

export function DraftFirst({ sessionId, entry, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [text, setText] = useState(entry.draft_text || "");
  const act = async (payload) => {
    if (busy) return;
    setBusy(true); setErr("");
    try { onUpdate(await writingPathEntry(sessionId, payload)); }
    catch (e) { const d = e?.response?.data?.detail; setErr(typeof d === "string" ? d : "Something went wrong."); }
    finally { setBusy(false); }
  };
  const understanding = entry.draft_stage === "understand";
  return (
    <section data-testid="wp-draft-first" className="bg-white border border-stone-200 rounded-md p-6">
      <h1 className="font-serif-display text-3xl text-stone-900 mb-2">Start with what you've written</h1>
      <p className="text-[13px] text-[#8C3A2A] mb-1">Rethinking and developing the paper from an early draft — not polishing it yet.</p>
      <p className="text-[13px] text-stone-500 mb-4">{understanding ? entry.draft_understand_prompt : "Paste or type the writing you already have. It is kept exactly as you wrote it — writing is one way of finding out what you think."}</p>
      <WritingEditor testid="wp-draft-editor" initialText={entry.draft_text || ""} onChange={({ text: t }) => setText(t)} autoFocus={false} minHeightPx={220} placeholder="Paste or type your writing here…" />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button data-testid="wp-draft-save" onClick={() => act({ action: "set_draft", text })} disabled={busy || !text.trim() || text === entry.draft_text} className={ghost}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {entry.draft_text ? "Save changes" : "Keep this writing"}</button>
        {entry.draft_text && <button data-testid="wp-draft-understand" onClick={() => act({ action: "understand" })} disabled={busy} className={ghost}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} What is this writing saying?</button>}
        <span className="flex-1" />
        {entry.draft_text && <button data-testid="wp-draft-to-thesis" onClick={() => act({ action: "to_thesis" })} disabled={busy} className={btn}>Say what the essay should say <ArrowRight className="h-4 w-4" /></button>}
      </div>
      {err && <p data-testid="wp-draft-error" className="mt-3 text-[13px] text-[#8C3A2A]">{err}</p>}
      {entry.draft_coach && <div data-testid="wp-draft-coach" className="mt-5 text-[14px] text-stone-800 bg-stone-50 border border-stone-200 rounded-sm p-4 leading-relaxed"><p className="font-mono-panel text-[10px] uppercase tracking-[0.22em] text-[#8C3A2A] mb-1">TWS</p>{entry.draft_coach}</div>}
    </section>
  );
}
